import { IDropdownOption, MessageBarType, Toggle } from '@fluentui/react';
import {
  Button,
  buttonStylesGhost,
  buttonStylesPrimary,
  Dropdown,
  hasChangedFieldValues,
  TextField,
  useClassNames,
  useToast,
} from '@h2oai/ui-kit';
import { FormEvent, useCallback, useEffect, useState } from 'react';

import { fsApi } from '../../featurestore/api';
import {
  CoreService,
  Feature,
  FeatureSet,
  UpdatableFeatureField,
  UpdateFeatureRequest,
  UpdateFeatureResponse,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { FeatureType } from '../../featurestore/gen/ai/h2o/featurestore/api/v1/FeatureSetProtoApi.pb';
import { ListRecommendationClassifierResponse } from '../../featurestore/gen/ai/h2o/featurestore/api/v1/RecommendationProtoApi.pb';
import { buttonStylesPrimaryPanel } from '../../featurestore/models';
import { getErrorMessageBarMessage } from '../../featurestore/utils';
import {
  editFeatureStylesDefault,
  IEditFeatureClassNames,
  IEditFeatureStyles,
  toggleStylesEditFeature,
} from './EditFeature.styles';

interface EditFeatureFields {
  description?: string;
  anomalyDetection?: boolean;
  status?: string;
  featureType?: FeatureType;
  importance?: number;
  special?: string;
  classifiers?: string[];
}

const getEditFeatureFields = (feature: Feature): EditFeatureFields => {
  const { description, status, monitoring, profile, importance, special, classifiers = [] } = feature;
  return {
    description,
    status,
    anomalyDetection: monitoring?.anomalyDetection,
    featureType: profile?.featureType,
    importance,
    special,
    classifiers,
  };
};

const featureTypeOptions: IDropdownOption[] = [
  { key: FeatureType.AUTOMATIC_DISCOVERY, text: 'Automatic Discovery' },
  { key: FeatureType.COMPOSITE, text: 'Composite' },
  { key: FeatureType.NUMERICAL, text: 'Numerical' },
  { key: FeatureType.CATEGORICAL, text: 'Categorical' },
  { key: FeatureType.TEMPORAL, text: 'Temporal' },
  { key: FeatureType.TEXT, text: 'Text' },
];

const getRequest = (
  previousValues: EditFeatureFields,
  values: EditFeatureFields,
  featureId?: string,
  featureSetId?: string,
  featureSetVersion?: string
): UpdateFeatureRequest => {
  const fieldMap = new Map<string, any>([
    ['description', UpdatableFeatureField.FEATURE_DESCRIPTION],
    ['anomalyDetection', UpdatableFeatureField.FEATURE_ANOMALY_DETECTION],
    ['status', UpdatableFeatureField.FEATURE_STATUS],
    ['featureType', UpdatableFeatureField.FEATURE_TYPE],
    ['importance', UpdatableFeatureField.FEATURE_IMPORTANCE],
    ['special', UpdatableFeatureField.FEATURE_SPECIAL],
    ['classifiers', UpdatableFeatureField.FEATURE_CLASSIFIERS],
  ]);
  const req: UpdateFeatureRequest = {
    featureId,
    featureSetId,
    featureSetVersion,
    fieldsToUpdate: [],
  };
  Object.keys(values).forEach((field) => {
    const newValue = values[field as keyof EditFeatureFields];
    const value = previousValues[field as keyof EditFeatureFields];
    if (value !== newValue) {
      req.fieldsToUpdate?.push(fieldMap.get(field));
      req[field as keyof UpdateFeatureRequest] = newValue;
    }
  });
  return req;
};

interface IEditFeatureProps {
  featureSetVersion: string;
  feature: Feature;
  onSave?: (updatedFeatureSet?: FeatureSet, updatedFeature?: Feature) => void;
  onCancel?: () => void;
}

export const EditFeature = ({ featureSetVersion, feature, onSave, onCancel }: IEditFeatureProps) => {
  const previousValues = getEditFeatureFields(feature);
  const [values, setValues] = useState<EditFeatureFields>({ ...previousValues });
  const [classifierOptions, setClassifierOptions] = useState<IDropdownOption[]>([]);
  const { addToast } = useToast();
  const load = useCallback(async () => {
    try {
      const { classifiers = [] }: ListRecommendationClassifierResponse = await fsApi(
        CoreService.ListRecommendationClassifiers
      );
      setClassifierOptions(classifiers.map((c) => ({ key: c.name || '', text: c.name || '' })));
    } catch (err) {
      addToast(getErrorMessageBarMessage(err, `An error has occurred while loading Classifiers: `));
    }
  }, [addToast]);
  const onUpdate = useCallback(async (updatedValues: EditFeatureFields) => {
    try {
      const { updatedFeatureSet, updatedFeature }: UpdateFeatureResponse = await fsApi(
        CoreService.UpdateFeature,
        getRequest(previousValues, updatedValues, feature.id, feature.featureSetId, featureSetVersion)
      );
      addToast({
        messageBarType: MessageBarType.success,
        message: 'Feature is Updated Successfully!',
      });
      onSave && onSave(updatedFeatureSet, updatedFeature);
    } catch (err) {
      addToast(getErrorMessageBarMessage(err, `An error has occurred while updating the feature: `));
      onCancel && onCancel();
    }
  }, []);
  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      onUpdate(values);
      e.preventDefault();
    },
    [values]
  );
  useEffect(() => {
    load();
  }, []);
  const classNames = useClassNames<IEditFeatureStyles, IEditFeatureClassNames>('EditFeature', editFeatureStylesDefault);
  return (
    <div className={classNames.root} data-test="edit-feature">
      <form className={classNames.form} onSubmit={onSubmit}>
        <div className={classNames.fields}>
          <TextField
            name="description"
            label="Description"
            multiline
            rows={3}
            defaultValue={previousValues.description}
            onChange={(_e, description) => {
              setValues({ ...values, description });
            }}
          />
          <Toggle
            styles={toggleStylesEditFeature}
            id="anomalyDetection"
            label="Anomaly Detection"
            defaultChecked={previousValues.anomalyDetection}
            onChange={(_e, anomalyDetection) => {
              setValues({ ...values, anomalyDetection });
            }}
          />
          <TextField
            name="status"
            label="Status"
            defaultValue={previousValues.status}
            onChange={(_e, status) => {
              setValues({ ...values, status });
            }}
          />
          <Dropdown
            label="Feature Type"
            placeholder="Select Feature Type"
            defaultSelectedKey={previousValues.featureType}
            options={featureTypeOptions}
            onChange={(_e, option) => {
              if (option) {
                setValues({ ...values, featureType: option.key as FeatureType });
              }
            }}
          />
          <TextField
            name="importance"
            label="Importance"
            type="number"
            defaultValue={String(previousValues.importance)}
            onChange={(_e, importance) => {
              if (importance !== undefined) setValues({ ...values, importance: +importance });
            }}
          />
          <TextField
            name="special"
            label="Special"
            defaultValue={previousValues.special}
            onChange={(_e, special) => {
              setValues({ ...values, special });
            }}
          />
          <Dropdown
            label="Classifiers"
            placeholder="Select Classifiers"
            multiSelect
            selectedKeys={values.classifiers}
            options={classifierOptions}
            onChange={(_e, option) => {
              if (option) {
                const name = String(option.key);
                const classifiers = option.selected
                  ? [...(values.classifiers || []), name]
                  : values.classifiers?.filter((key) => key !== name);
                setValues({ ...values, classifiers });
              }
            }}
          />
        </div>
        <div className={classNames.buttons}>
          <Button styles={buttonStylesGhost} text="Cancel" onClick={onCancel} style={{ marginRight: 8 }} />
          <Button
            styles={[buttonStylesPrimary, buttonStylesPrimaryPanel]}
            type="submit"
            text="Save"
            disabled={!hasChangedFieldValues(previousValues, values)}
          />
        </div>
      </form>
    </div>
  );
};
